import { Component, ElementRef, inject, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { QuantityCounts } from '../../../../feature-modules/welcomewizard/quantity-counts';
import { CartService } from '../../../services/cart/cart.service';
import { LoggedInInfoService } from '../../../services/loggedInInfo/logged-in-info.service';
import { NotificationDialogService } from '../../../services/notificationDialog/notification-dialog.service';
import { OrganizationLogicService } from '../../../services/organization-logic/organization-logic.service';
import { PackagesService } from '../../../services/packages/packages.service';
import { RefreshService } from '../../../services/refresh/refresh.service';
import {
  RnLicenseFullVM,
  RnOrganizationConfigurationVM,
  RnOrganizationPaymentOnFileVM,
  RnOrganizationsProfileVM,
  RnOrganizationWLPContactInformationVM,
  RnUserCreate,
  RnUserCreateFull,
  RnUserLicensesAssignUser,
  RnUserPackageAssign,
  RnUserPackagePoolAction
} from '../../../services/rnapi2-service/models/models';
import { SelectionService } from '../../../services/selection/selection.service';
import { allowedEmailValidator } from '../../../validation/allowed-email-validator.service';
import { MustMatch } from '../../../validation/must-match.validator';
import { GenericDialogConfiguration } from '../generic-dialog/generic-dialog-configuration';
import { PackageCardData } from '../package-card/package-card-data';
import { AddUsersModalService } from './add-users-modal.service';
import { AddUserData } from './add-user-data';
import { ModalComponent } from '@rn-platform/frontend-shared-ui-common';
import { AddUserSteps } from './add-user-steps';
import { StepInfo } from './step-info';

@Component({
  selector: 'app-add-user',
  templateUrl: './add-user.component.html',
  styleUrls: ['./add-user.component.scss'],
  standalone: false
})
export class AddUserComponent implements OnInit, OnDestroy {
  private formBuilder = inject(FormBuilder);
  private organizationLogicService = inject(OrganizationLogicService);
  private loggedInInfoService = inject(LoggedInInfoService);
  private notificationDialogService = inject(NotificationDialogService);
  private renderer = inject(Renderer2);
  private cartService = inject(CartService);
  private packagesService = inject(PackagesService);
  private selectionService = inject(SelectionService);
  private readonly refreshService = inject(RefreshService);

  @ViewChild('modalRef') modalComponent: ModalComponent;
  @ViewChild('cartIcon') cartIcon: ElementRef;
  @ViewChild('cart') cart: ElementRef;
  private readonly addUserModalService = inject(AddUsersModalService);
  private unlisten: () => void = null;
  private selectedLicense: RnLicenseFullVM;
  private subscriptions: Subscription[] = [];
  private validatedPromo: any = {};
  private licenses: RnLicenseFullVM[];
  private pendingValidation = false;
  private userLicense: FormGroup;
  protected OrganizationID: number;
  protected isShoppingCartVisible = false;
  protected paymentInfo: RnOrganizationPaymentOnFileVM;
  protected checkForInitialActiveMessage = false;
  protected addUserData: AddUserData;
  protected orgProfile: RnOrganizationsProfileVM;
  protected orgConfig: RnOrganizationConfigurationVM;
  protected orgWlpContactInfo: RnOrganizationWLPContactInformationVM;
  protected noLicenses = true;
  protected nextBtnLabel = 'Next';
  protected nextBtnEnabled = false;
  protected hasAccountOwner: boolean;
  public pendingLicenseCheck = false;
  public addOns: PackageCardData[] = [];
  public selectedPackage: PackageCardData;
  public mainForm: FormGroup;
  public AllActions: QuantityCounts[] = [];
  public OwnerActions: QuantityCounts[] = [];
  public addOnsList = [];
  public addUserSteps: AddUserSteps;
  public packages: PackageCardData[];
  public availableAddOns: PackageCardData[];
  public isActive: boolean = false;
  public addUserModalSubscription: Subscription;

  constructor() {
    this.subscriptions.push(
      this.selectionService.SelectedOrgProfile.subscribe((p) => {
        this.orgProfile = p;
      })
    );

    this.subscriptions.push(
      this.loggedInInfoService.LoggedInUserOrgPaymentOnFile.subscribe((r) => {
        this.paymentInfo = r;
      })
    );
  }

  ngOnInit(): void {
    this.addUserModalSubscription = this.addUserModalService.modalState$.subscribe((data) => {
      this.subscribeModalService(data);
    });
  }

  subscribeModalService(data: any): void {
    if (!data?.config) {
      return;
    }

    if (data?.isOpen) {
      this.addUserSteps = this.generateFormSteps();
      this.addUserData = data.config;
      this.OrganizationID = this.addUserData?.ID;
      this.orgConfig = this.addUserData?.OrgConfig;
      this.orgWlpContactInfo = this.addUserData?.OrgWlpContactInfo;
      this.subscribeCartMouseListener();
      this.generateMainForm();
      this.setUserLicenseValidators();
      this.hasAccountOwner = this.organizationLogicService.DoesSelectedOrgPayForItself();
      this.refreshService.refreshPOF(() => {});
      this.isActive = true;
      this.open();
    } else 
    {
      this.isActive = false;
      this.unsubscribeCartMouseListener();
      this.close();
    }
  }

  subscribeCartMouseListener(): void {
    this.unlisten = this.renderer.listen('window', 'click', (e: Event) => {
      if (
        this.addUserData?.UserActions?.length > 0 &&
        this.addUserData?.OwnerActions?.length > 0 &&
        !this.cartIcon?.nativeElement.contains(e.target) &&
        !this.cart?.nativeElement.contains(e.target)
      ) {
        this.forceCloseShoppingCart();
      }
    });
  }

  unsubscribeCartMouseListener(): void {
    if (this.unlisten) {
      this.unlisten();
      this.unlisten = null;
    }
  }

  generateMainForm(): void {
    const userType: string = this.loggedInInfoService.GetLoggedInUser().UserType;
    this.userLicense = this.formBuilder.group({
        isDeploy: [true],
        qbVersion: ['', Validators.required.bind(Validators)],
        qbCountry: ['', Validators.required.bind(Validators)],
        qbEdition: ['', Validators.required.bind(Validators)],
        qbFlavor: ['', Validators.required.bind(Validators)]
    });

    this.mainForm = this.formBuilder.group({
      UserCreate: this.formBuilder.group(
        {
          role: ['', Validators.required.bind(Validators)],
          firstName: ['', Validators.required.bind(Validators)],
          lastName: ['', Validators.required.bind(Validators)],
          email: [
            '',
            [
              Validators.required.bind(Validators),
              Validators.pattern(
                /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
              ),
              allowedEmailValidator('rightnetworks.com', userType)
            ]
          ],
          confirmEmail: ['', Validators.required.bind(Validators)],
          sendWelcomeEmail: '',
          xid: [null, Validators.maxLength(50)]
        },
        {
          validator: MustMatch('email', 'confirmEmail')
        }
      ),
      UserPackage: this.formBuilder.group({
        selectedPackage: ['', Validators.required.bind(Validators)]
      }),
      UserAddons: this.formBuilder.group({
        selectedAddons: [[], Validators.required.bind(Validators)]
      }),
      UserLicense: this.userLicense
    });
  }

  generateFormSteps(): AddUserSteps {
    const steps: AddUserSteps = new AddUserSteps();
    const userStep: StepInfo = new StepInfo({
      ID: 'USER',
      title: 'User Details',
      formName: 'UserCreate',
      desc: 'This is where you can select the role and details for this user. On the next set of screens you will be able to select the package and add-ons that they will need to use in the hosted desktop environment.'
    });
    steps.push(userStep);
    const packageStep: StepInfo = new StepInfo({
      ID: 'PKG',
      title: 'Select a Package',
      formName: 'UserPackage',
      desc: "Select one of the options below based on the user's role and needs."
    });
    steps.push(packageStep);
    const addonStep: StepInfo = new StepInfo({
      ID: 'ADDON',
      title: 'Select Add-ons',
      desc: 'If needed, we will automatically purchase a copy of an add-on for you.',
      isDisabled: () => this.noAvailableAddons()
    });
    steps.push(addonStep);
    const licenseStep: StepInfo = new StepInfo({
      ID: 'LICENSE',
      title: 'Deploy QuickBooks',
      formName: 'UserLicense',
      desc: "We can put QuickBooks on this user's cloud-hosted desktop so they can get started right away. You can install any version of QuickBooks that your organization has a license for. <br/>If you bought or leased a license from Rightworks, it may take up to 48 hours for it to appear on your account.",
      isDisabled: () => (this.isNoPackageSelected() || this.hasNoLicenses())
    });
    steps.push(licenseStep);
    const reviewStep: StepInfo = new StepInfo({
      ID: 'REVIEW',
      title: 'Review Details',
      btnText: 'ADD USER'
    });
    steps.push(reviewStep);
    return steps;
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => {
      sub?.unsubscribe();
    });
    if (this.unlisten) {
      this.unlisten();
    }
    this.addUserModalSubscription?.unsubscribe();
  }

  public forceCloseShoppingCart(): void {
    this.isShoppingCartVisible = false;
  }

  public toggleShoppingCart(): void {
    this.isShoppingCartVisible = !this.isShoppingCartVisible;
  }

  private resetAddOns(): void {
    const addonActions: QuantityCounts[] = [];
    this.addOns.forEach((a) => {
      if (a.AddOnAdded) {
        a.AddOnAdded = false;
        if (a.BuyNew) {
          this.cartService.removeFromCart(a);
          a.BuyNew = false;
          a.CurrentQuantity--;
        }
        addonActions.push({
          PackageID: a.PackageID,
          Quantity: -1
        });
        a.CurrentUsed--;
      }
    });
  }

  public getNextLabel(): string {
    return this.addUserSteps.isLastStep() ? 'ADD USER' : 'SAVE';
  }

  public hasNoLicenses(): boolean {
    return this.licenses?.length === 0;
  }

  public cancelAddUser(): void {
    const confirmConfig = new GenericDialogConfiguration();
    confirmConfig.Title = "Don't Create User?";
    confirmConfig.StyleClass = 'confirmation';
    confirmConfig.ConfirmButtonText = "Don't Create User";
    confirmConfig.CancelButtonText = 'Cancel';
    confirmConfig.CancelButtonStyleClass = 'cancelButton';
    confirmConfig.MessageContainsHTML = true;
    confirmConfig.ConfirmButtonStyleClass = 'dontCreateUserButton';
    confirmConfig.showConfirmIcon = true;
    confirmConfig.confirmImageIconClass = 'deleteImage';
    confirmConfig.showConfirmIconImageSource = 'rn-trash-2';
    confirmConfig.DialogHeaderClass = 'modal-header no-border';
    confirmConfig.DialogFooterCancelClass = 'right-spacing';
    confirmConfig.Message =
      '<p class="m-b-1rem modalBody">You are in the process of adding a new user. If you end the process now, the user will not be added and you will need to add them again.</p><p class="m-b-1rem modalBody">Click Don\'t Create User to end the process now and go back to the list of users.</p><p class="modalBody">Click Cancel to close this dialog and go back to adding the user.</p>';
    confirmConfig.Confirmed = () => {
      this.close();
    };
    this.notificationDialogService.ShowConfirmation(confirmConfig);
  }

  public pendingValidationChanged(value: boolean) {
    this.pendingValidation = value;
  }

  public packagesLoaded(packages: PackageCardData[]) {
    this.packages = packages;
  }

  public addonsLoaded(addons: PackageCardData[]) {
    this.addOns = addons;
  }

  public packageSelected(pkg: PackageCardData) {
    this.selectedPackage = pkg;
    this.resetAddOns();
    // Stuff was removed from here that originally checked for an
    // assigned package, because now we have some add-ons that don't need a package
    const notAvailAddOns = this.addOns.filter((a) => {
      return (
        a.ExcludingPackageIds?.filter((i) => {
          return i === this.selectedPackage.PackageID;
        }).length > 0 && a.AddOnAdded
      );
    });
    notAvailAddOns?.forEach((a) => {
      if (a.AddOnAdded) {
        a.AddOnAdded = false;
        if (a.BuyNew) {
          this.cartService.removeFromCart(a);
          a.BuyNew = false;
          a.CurrentQuantity--;
        }
        a.CurrentUsed--;
      }
    });
    this.availableAddOns = this.getAvailableAddons(this.isNoPackageSelected());
  }

  public isNoPackageSelected(): boolean {
    return this.selectedPackage?.PackageID < 0;
  }

  getAvailableAddons(noPackageSelected: boolean): PackageCardData[] {
    if (noPackageSelected) {
      return this.addOns?.filter((a) => {
        return a?.NonHostedDesktop;
      });
    } else {
      return this.addOns?.filter((a) => {
        return (
          a?.ExcludingPackageIds?.filter((i) => {
            return i === this.selectedPackage?.PackageID;
          }).length === 0
        );
      });
    }
  }

  public setUserLicenseValidators(): void {
    const versionControl = (this.mainForm.get('UserLicense') as FormGroup).controls.qbVersion;
    const countryControl = (this.mainForm.get('UserLicense') as FormGroup).controls.qbCountry;
    const editionControl = (this.mainForm.get('UserLicense') as FormGroup).controls.qbEdition;
    const flavorControl = (this.mainForm.get('UserLicense') as FormGroup).controls.qbFlavor;

    this.subscriptions.push(
      (this.mainForm.get('UserLicense') as FormGroup).controls.isDeploy.valueChanges.subscribe((isDeploy) => {
        if (isDeploy == 'true') {
          versionControl.setValidators([Validators.required.bind(Validators)]);
          countryControl.setValidators([Validators.required.bind(Validators)]);
          editionControl.setValidators([Validators.required.bind(Validators)]);
          flavorControl.setValidators([Validators.required.bind(Validators)]);
        } else {
          versionControl.setValidators(null);
          countryControl.setValidators(null);
          editionControl.setValidators(null);
          flavorControl.setValidators(null);
        }

        versionControl.updateValueAndValidity();
        countryControl.updateValueAndValidity();
        editionControl.updateValueAndValidity();
        flavorControl.updateValueAndValidity();
      })
    );
  }

  public createUser(): void {
    const userCreateGroup = this.mainForm?.get('UserCreate') as FormGroup | null;
    const userCreateControls = userCreateGroup?.controls ?? null;
    if (!userCreateControls) {
      console.warn('there was a problem loading user creation form controls.');
      return;
    }
    const newUserPayload: RnUserCreate = {
      RNOrganizationID: this.OrganizationID,
      Email: userCreateControls.email?.value,
      FirstName: userCreateControls.firstName?.value,
      LastName: userCreateControls.lastName?.value,
      SendWelcomeEmail: userCreateControls.sendWelcomeEmail?.value,
      UserTypeID: userCreateControls.role?.value?.ID,
      XID: userCreateControls.xid?.value
    };

    const packageSelectGroup = (this.mainForm?.get('UserPackage') as FormGroup) || null;
    const packageSelectControls = packageSelectGroup ? packageSelectGroup.controls : null;
    if (!packageSelectControls) {
      console.warn('there was a problem loading user creation form controls.');
      return;
    }
    const packagePayload: RnUserPackageAssign = {
      AffectedOrganizationId: this.orgProfile?.IdThatPaysForThisOrg,
      AffectedUserId: -1,
      PackagePool_ID: packageSelectControls?.selectedPackage?.value?.PackagePoolID,
      Package_ID: packageSelectControls?.selectedPackage?.value?.PackageID,
      User_ID: -1
    };

    const addonSelectControls = (this.mainForm?.get('UserAddons') as FormGroup).controls;
    const addonPayloads: Array<RnUserPackagePoolAction> | null = [];
    const addons = addonSelectControls.selectedAddons.value as PackageCardData[];
    if (addons) {
      addons.forEach((a) => {
        const action = new RnUserPackagePoolAction();
        action.AffectedOrganizationId = this.orgProfile.IdThatPaysForThisOrg;
        action.ToPackage_ID = a.PackageID;
        action.AtomicAction = this.getAtomicAction(a);
        action.KeepOldPackage = true;
        action.PromotionCodeID =
          this.validatedPromo &&
          this.validatedPromo.promoCode &&
          this.validatedPromo.promoCode?.PackageID == a.PackageID
            ? this.validatedPromo.promoCode?.ID
            : null;
        addonPayloads.push(action);
      });
    }

    let licensePayload: RnUserLicensesAssignUser;
    if (this.mainForm?.get('UserLicense')?.get('isDeploy')?.value === 'false') {
      licensePayload = {
        AffectedOrganizationId: this.orgProfile.IdThatPaysForThisOrg,
        LicenseID: 0,
        FlavorID: 0,
        UserIDs: []
      };
    } else {
      licensePayload = {
        AffectedOrganizationId: this.orgProfile.IdThatPaysForThisOrg,
        LicenseID: this.selectedLicense ? this.selectedLicense.LicenseID : 0,
        FlavorID: this.selectedLicense ? this.selectedLicense.FlavorId : 0, // ItemFlavorID doesn't work here, don't put it back. I will find you.
        UserIDs: []
      };
    }
    let promoID = this.validatedPromo?.promoCode?.ID ?? null;
    if (!promoID) {
      promoID = this.cartService.promo?.promoCode?.ID ?? null;
    }

    const fullPayload: RnUserCreateFull = {
      UserCreate: newUserPayload,
      UserPackage: packagePayload,
      UserLicense: licensePayload,
      UserAddons: addonPayloads || [],
      PromotionCodeID: promoID
    };
    this.addUserModalService.emitSubmitStatus(fullPayload);
    this.close();
  }

  public getTitle(index?: number): string {
    if (index) {
      return this.addUserSteps.getStepByIndex(index)?.title ?? '';
    }
    return this.addUserSteps.getActiveStep()?.title ?? '';
  }

  public getDescription(index?: number): string {
    if (index) {
      return this.addUserSteps.getStepByIndex(index)?.desc ?? '';
    }
    return this.addUserSteps.getActiveStep()?.desc ?? '';
  }

  public getStepID(index?: number): string {
    if (index) {
      return this.addUserSteps.getStepByIndex(index)?.ID ?? '';
    }
    return this.addUserSteps.getActiveStep()?.ID ?? '';
  }

  public getButtonText(index?: number): string {
    if (index) {
      return this.addUserSteps.getStepByIndex(index)?.btnText ?? '';
    }
    return this.addUserSteps.getActiveStep()?.btnText ?? '';
  }




  public close(): void {
    this.isActive = false;
    this.selectedLicense = undefined;
    this.subscriptions = [];
    this.validatedPromo = undefined;
    this.licenses = [];
    this.pendingValidation = false;
    this.userLicense.patchValue({
      qbVersion: '',
      qbCountry: '',
      qbEdition: '',
      qbFlavor: '',
      isDeploy: false
    });
    this.isShoppingCartVisible = false;
    this.paymentInfo = undefined;
    this.checkForInitialActiveMessage = false;
    this.addUserData = undefined;
    this.noLicenses = true;
    this.nextBtnLabel = 'Next';
    this.nextBtnEnabled = false;
    this.hasAccountOwner = undefined;
    this.pendingLicenseCheck = false;
    this.addOns = [];
    this.AllActions = [];
    this.OwnerActions = [];
    this.addOnsList = []
    this.addUserSteps = undefined;
    this.packages = undefined;
    this.availableAddOns = undefined;
    this.isActive = false;
    this.cartService?.clearCart();
    this.modalComponent?.close();
  }

  public open(): void {
    this.isActive = true;
    this.modalComponent?.open();
  }

  private getAtomicAction(pkg: PackageCardData): string {
    if (pkg.PackagePoolID == -2) {
      return 'nopackage';
    }
    // else, this is a new user so its simple. Either an add or a "reassign"
    if (!pkg.BuyNew) {
      return 'reassign';
    } else {
      return 'add';
    }
  }

  public handleNext(): void {
    if (this.addUserSteps.isLastStep()) {
      this.createUser();
    } else {
      this.addUserSteps.iterateStep();
    }
  }

  public handlePrevious(): void {
    if (this.addUserSteps.isFirstStep()) {
      return;
    } else {
      this.addUserSteps.iterateStep({ reverse: true });
    }
  }

  public canNavigateToNextStep(): boolean {
    if (!this.mainForm) {
      return false;
    }
    if (this.addUserSteps.isLastStep()) {
      return true;
    }
    const step: StepInfo = this.addUserSteps.getActiveStep();
    switch (step.ID) {
      case 'USER':
        return this.isUserFormValid();
      case 'PKG':
        return this.isPackageValid(step);
      case 'ADDON':
        return true;
      case 'LICENSE':
        return this.isLicenseValid();
      case 'REVIEW':
        return true;
      default:
        return false;
    }
  }

  private isPackageValid(currentStep: StepInfo): boolean {
    const step: StepInfo = this.addUserSteps.getStepByID('PKG');
    if (!step || step.isDisabled()) {
      return true;
    } else if (this.packages?.length > 0) {
      const formName = currentStep.formName;
      const userPackage = this.mainForm?.controls[formName]?.value;
      if (userPackage?.selectedPackage) {
        return this.mainForm.get(formName).valid && !this.pendingValidation;
      }
    }
    return false;
  }

  private noAvailableAddons(): boolean {
    this.availableAddOns = this.getAvailableAddons(this.selectedPackage.PackageID >= 0);
    if (this.availableAddOns.length === 0) {
      return true;
    }
    return false;
  }

  private isLicenseValid(): boolean {
    const licenseStep = this.addUserSteps.getStepByID('LICENSE');
    if (!licenseStep || licenseStep.isDisabled()) {
      return true;
    }

    const licenseFormGroup: FormGroup | null = this.mainForm?.get(licenseStep.formName) as FormGroup;
    if(this.userLicense?.invalid){
      return false;
    }
    const addOnStep = this.addUserSteps.getStepByID('ADDON');
    let addonsToCheck: PackageCardData[];
    const addonFormGroup: FormGroup | null = this.mainForm?.get(addOnStep?.formName) as FormGroup;
    if (addonFormGroup) {
      addonsToCheck =
        (addonFormGroup?.controls?.selectedAddons?.value as PackageCardData[]) ?? new Array<PackageCardData>();
      if (!this.packagesService.isApplicableDeployQuickBooks(this.selectedPackage, this.licenses, addonsToCheck)) {
        return false;
      }
    } else if (licenseFormGroup) {
      const isDeployValue = licenseFormGroup.get('isDeploy')?.value;
      if (isDeployValue === 'false') {
        return true;
      }
      if (this.pendingLicenseCheck) {
        return false;
      }
    } else if (this.selectedPackage?.PackageID > 0 && !this.noLicenses) {
      const packageStep = this.addUserSteps.getStepByID('PKG');
      if ((!addOnStep || addOnStep.isDisabled()) && (!packageStep || packageStep.isDisabled())) {
        return true;
      }
    }
    return true;
  }

  private isUserFormValid(): boolean {
    if (!this.mainForm) return false;
    const userStep = this.addUserSteps.getStepByID('USER');
    const userFormGroup = this.mainForm.get(userStep?.formName) as FormGroup;
    if (!userFormGroup) return false;
    const emailControl = userFormGroup.get('email');
    return (
      userFormGroup.valid &&
      !userFormGroup.pending &&
      emailControl?.valid &&
      !emailControl?.pending &&
      !emailControl?.errors?.emailTaken
    );
  }

  public pendingLicenseCheckEvent(value: boolean) {
    this.pendingLicenseCheck = value;
  }

  public noLicenseEvent(value: boolean) {
    this.noLicenses = value;
  }

  public licensesEvent(value: RnLicenseFullVM[]) {
    this.licenses = value;
  }

  public selectedLicenseEvent(license: RnLicenseFullVM) {
    this.selectedLicense = license;
  }

  public promoEvent(value: any) {
    this.validatedPromo = value;
  }
}
